import { classNames } from "../utils";
import {
  Item,
  ItemTypes,
  ItemWithMetaData,
  ProfileAddedBy,
  Type,
} from "../api/Items";
import ProfileDisplay from "./ProfileDisplay";

type ConfirmedProfilesProps = {
  profileList: ItemWithMetaData[];
  isReadOnly: boolean;
  handleAddProfile?: (inputUrl: string) => void;
  handleDeleteProfile?: (itemUUID: string) => void;
  errorMessage?: string;
  isSidebar?: boolean;
};

const ConfirmedProfiles = ({
  profileList,
  isReadOnly,
  handleAddProfile,
  handleDeleteProfile,
  errorMessage,
  isSidebar,
}: ConfirmedProfilesProps) => {
  const systemAddedProfiles = filterProfiles(profileList, false, true);

  const clientAddedProfiles = filterProfilesByAddedBy(
    profileList,
    ProfileAddedBy.CLIENT
  );

  const investigatorAddedProfiles = isSidebar
    ? filterInvestigatorAddedProfiles(profileList)
    : [];
  const isEmptyInvestigatorAddedProfiles = investigatorAddedProfiles.length === 0;

  const isEmptySystemAddedProfiles = systemAddedProfiles.length === 0;
  const isEmptyClientAddedProfiles = clientAddedProfiles.length === 0;

  return (
    <div className={isSidebar ? "p-4" : ""}>
      <div className={classNames(`font-semibold mb-6`, isSidebar ? "text-lg" : "text-2xl")}>Confirmed Profiles</div>
      <div className="mx-auto rounded-lg p-4 bg-gray-50 shadow-sm border border-gray-200">
        <>
          <div className="font-semibold">System Confirmed</div>
          {isEmptySystemAddedProfiles && (
            <div className="text-gray-500 text-sm py-2">
              No profiles were added by the System
            </div>
          )}
          {!isEmptySystemAddedProfiles && (
            <ProfileDisplay
              isManualAdd={false}
              profileList={systemAddedProfiles}
              tabColor="blue"
              isReadOnly={true}
              onDelete={handleDeleteProfile}
              isSidebar={isSidebar}
              showAddedBy={false}
            />
          )}
        </>
        <>
          <div className="font-semibold">Client Added</div>
          {isEmptyClientAddedProfiles && (
            <div className="text-gray-500 text-sm py-2">
              No profiles were added by Clients
            </div>
          )}
          {!isEmptyClientAddedProfiles && (
            <ProfileDisplay
              isManualAdd={false}
              profileList={clientAddedProfiles}
              tabColor="blue"
              isReadOnly={true}
              onDelete={handleDeleteProfile}
              isSidebar={isSidebar}
              showAddedBy={false}
            />
          )}
        </>
        {isSidebar && (
          <>
            <div className="font-semibold">Investigator Added</div>
            {isEmptyInvestigatorAddedProfiles && (
              <div className="text-gray-500 text-sm py-2">
                No profiles were added by Investigator
              </div>
            )}
            {!isEmptyInvestigatorAddedProfiles && (
              <ProfileDisplay
                isManualAdd={false}
                profileList={investigatorAddedProfiles}
                tabColor="blue"
                isReadOnly={true}
                onDelete={handleDeleteProfile}
                isSidebar={isSidebar}
                showAddedBy={false}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};

const filterProfilesByAddedBy = (
  profilesList: ItemWithMetaData[],
  addedBy: ProfileAddedBy
) => {
  if (!profilesList) {
    return [];
  }
  const filtered: ItemWithMetaData[] = [];
  profilesList.forEach((itemWithMetaData: ItemWithMetaData) => {
    if (itemWithMetaData.item.content?.added_by === addedBy) {
      filtered.push(itemWithMetaData);
    }
  });
  return filtered;
};

const filterInvestigatorAddedProfiles = (
  profilesList: ItemWithMetaData[]
): ItemWithMetaData[] => {
  if (!profilesList) {
    return [];
  }
  const filtered: ItemWithMetaData[] = [];
  profilesList.forEach((itemWithMetaData: ItemWithMetaData) => {
    const type: Type = itemWithMetaData.type;
    const item: Item = itemWithMetaData.item;
    const itemIsProfile = type.name === ItemTypes.SOCIAL_MEDIA_PROFILE;
    const addedBy = item.content?.added_by;
    const isInvestigatorAdded =
      item.manually_added === true ||
      item.is_reviewed === true ||
      addedBy === ProfileAddedBy.INVESTIGATOR;
    if (
      itemIsProfile &&
      item.in_report &&
      addedBy !== ProfileAddedBy.CLIENT &&
      isInvestigatorAdded
    ) {
      filtered.push(itemWithMetaData);
    }
  });
  return filtered;
};

const filterProfiles = (
  profilesList: ItemWithMetaData[],
  manuallyAdded: boolean,
  justSystem: boolean
) => {
  if (!profilesList) {
    return [];
  }
  const filtered: ItemWithMetaData[] = [];
  profilesList.forEach((itemWithMetaData: ItemWithMetaData) => {
    let type: Type = itemWithMetaData.type;
    let item: Item = itemWithMetaData.item;
    const itemIsProfile: boolean = type.name === ItemTypes.SOCIAL_MEDIA_PROFILE;

    if (
      itemIsProfile &&
      item.manually_added === manuallyAdded &&
      item.in_report &&
      justSystem &&
      !item.is_reviewed
    ) {
      filtered.push(itemWithMetaData);
    }
  });
  return filtered;
};

export default ConfirmedProfiles;
